import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { CircularProgress } from '@material-ui/core';
import { lighten } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { DATE_RANGE_SELECTED, getDateRangeRadioOptions } from '../ReportsUtils';
import { JANUARY, MONTHS_OPTIONS } from '../../../../utils/dates-const';
import { BACKGROUND_WHITE, BLACK_COLOR, DARK_BORDER_COLOR, LIGHT_GREY_COLOR } from '../../../../const/color-const';
import {
  SEARCH_BUTTON_WIDTH, STANDARD_INPUT_HEIGHT, STANDARD_INPUT_RADIUS, STANDARD_INPUT_WIDTH, STANDARD_MOBILE_INPUT_HEIGHT,
} from '../../../../const/chart-const';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import { fetchStandardReports } from '../../../../actions/report-actions';
import CommonButton from '../../../common/CommonButton';
import LoadingSnackbar from '../../../common/LoadingSnackbar';
import CommonMultiSelectField from '../../../common/form/CommonMultiSelectField';
import FormTextField from '../../../common/form/FormTextField';
import { upUntilCurrentMonthOnly } from '../../../../utils/dates-utils';

export const sie4Styles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  formContent: {
    width: '100%',
    display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& div': {
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
      '& .commonSelectField': {
        width: STANDARD_INPUT_WIDTH,
        backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
        maxHeight: 60,
        height: '100%',
      },
    },
    '& > div:nth-child(2)': {
      '& .commonSelectField': {
        margin: theme.spacing(0, 2, 0, 0),
      },
    },
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    maxWidth: '736px',
    width: '100%',
    '& button': {
      marginLeft: 0,
      marginRight: 0,
    },
    '& > button:nth-child(2)': {
      width: '180px',
    },
  },
  infoText: {
    maxWidth: theme.breakpoints.values.sm * 2,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  datePickersWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: STANDARD_INPUT_WIDTH,
      height: STANDARD_INPUT_HEIGHT,
      margin: theme.spacing(1, 2, 1, 0),
      borderRadius: STANDARD_INPUT_RADIUS,
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      border: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${DARK_BORDER_COLOR}`,
      '&:hover': {
        border: theme.palette.type === 'light' ? `1px solid ${BLACK_COLOR}` : `1px solid white`,
      },
      '& label': {
        border: 'none',
        marginLeft: 10,
      },
      '& input': {
        marginLeft: 10,
        border: 'none',
        height: '100%',
      },
      '& div': {
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
      },
    },
  },
  loadingBar: {
    marginTop: theme.spacing(2),
  },
  fieldsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '& > div:first-of-type': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      height: STANDARD_MOBILE_INPUT_HEIGHT,
      marginTop: theme.spacing(1),
      border: `1px solid ${LIGHT_GREY_COLOR}`,
      [theme.breakpoints.up('md')]: {
        height: STANDARD_INPUT_HEIGHT,
        width: STANDARD_INPUT_WIDTH,
      },
      '& label': {
        border: 'none',
        marginLeft: 10,
      },
      '& > div': {
        width: '100%',
        '& input': {
          marginLeft: 10,
          fontSize: 14,
          [theme.breakpoints.up('md')]: {
            fontSize: 16,
          },
        },
      },
      '& div': {
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
      },
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
    '& button': {
      width: SEARCH_BUTTON_WIDTH,
    },
  },
}));

const Sie4ReportForm = ({ reportName, machinesOptions, setFormValues, fetchStandardReports }) => {
  const [dateRangeOption, setDateRangeOption] = useState(DATE_RANGE_SELECTED);
  const [loadingProgress, setLoadingProgress] = useState();
  const MONTHS_THIS_YEAR = upUntilCurrentMonthOnly(MONTHS_OPTIONS);
  const classes = sie4Styles();
  
  const prepareValues = (values) => {
    const formValuesWithoutDateRange = { ...values };
    delete formValuesWithoutDateRange.dateRange;
    return formValuesWithoutDateRange;
  };
  
  return (
    <>
      <Formik
        initialValues={{
          machineIDs: [null],
          dateFrom: JANUARY.option.dateFrom,
          dateTo: JANUARY.option.dateTo,
          dateRange: JANUARY.value,
        }}
        onSubmit={(values, formikHelpers) => {
          setFormValues(values);
          fetchStandardReports(reportName, prepareValues(values), formikHelpers, setLoadingProgress);
        }}>
        
        {({ isSubmitting, values, isValid, submitForm, setFieldValue }) => {
          return (
            <Form className={classes.form}>
              <p>Sie4ReportForm</p>
              <div className={classes.formContent}>
                <Field
                  wide
                  atLeastOneElement
                  name="machineIDs"
                  label={<FormattedMessage
                    id="field.cash.register"
                    defaultMessage="Cash Register"
                  />}
                  options={[...machinesOptions, {
                    label: <>- <FormattedMessage
                      id="all"
                      defaultMessage="All"
                    /> -</>, value: null,
                  }]}
                  component={CommonMultiSelectField}
                />
                
                <div className={classes.fieldsWrapper}>
                  <Field
                    variant="standard"
                    name="Fritext"
                    component={FormTextField}
                    label={<FormattedMessage
                      id="field.free.text"
                      defaultMessage="Free text"
                    />}
                  />
                  {getDateRangeRadioOptions(setDateRangeOption, values, setFieldValue, dateRangeOption, classes, MONTHS_THIS_YEAR)}
                </div>
              </div>
              
              {isSubmitting && <CircularProgress className={classes.loadingBar} />}
              
              <div className={classes.buttonWrapper}>
                <CommonButton
                  isAnimated
                  onClick={() => isValid && submitForm()}
                  size="large"
                  label={
                    <FormattedMessage
                      id="search"
                      defaultMessage="Search"
                    />}
                  disabled={isSubmitting || !values.machineIDs || !values.dateFrom || !values.dateTo || !isValid}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
      
      <LoadingSnackbar
        progress={loadingProgress}
        label={<FormattedMessage id="loading" defaultMessage="Loading" />}
      />
    </>
  );
};

Sie4ReportForm.propTypes = {
  reportName: PropTypes.string,
  machines: PropTypes.array,
  fetchStandardReports: PropTypes.func,
  setFormValues: PropTypes.func,
};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }),
    dispatch => bindActionCreators({ fetchStandardReports }, dispatch)),
);

export default decorate(Sie4ReportForm);
